import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Autocomplete,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import colors from '../../../shared-colors';
import Button from '../../../components/Button';
import { createUserDoctorVisit, getProfessionalCaregiver } from '../../../utils/api';
import useStore from '../../../Store/Store';
import { useQuery } from 'react-query';
import { VISIT_TYPES, COMMON_SYMPTOMS } from '../utils/visitUtils';
import { useTranslation } from 'react-i18next';

const AddModal = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const { data: professionalCaregiver, isLoading } = useQuery({
    queryKey: ['professionalCaregiver', user?._id],
    queryFn: () => getProfessionalCaregiver({ userId: user?._id }),
  });
  const professionalCaregiverData = professionalCaregiver?.data;
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    doctorId: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    visitType: '',
    symptoms: [],
    timezone: new Date().getTimezoneOffset() / -60, // Get browser timezone offset in hours
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleSymptomAdd = (symptom) => {
    if (symptom && !formData.symptoms.includes(symptom)) {
      setFormData((prev) => ({
        ...prev,
        symptoms: [...prev.symptoms, symptom],
      }));
    }
  };

  const handleSymptomRemove = (symptomToRemove) => {
    setFormData((prev) => ({
      ...prev,
      symptoms: prev.symptoms.filter((symptom) => symptom !== symptomToRemove),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t('titleRequired');
    }

    if (!formData.doctorId.trim()) {
      newErrors.doctorId = t('doctorRequired');
    }

    if (!formData.startDate) {
      newErrors.startDate = t('startDateRequired');
    }

    if (!formData.endDate) {
      newErrors.endDate = t('endDateRequired');
    }

    if (!formData.startTime) {
      newErrors.startTime = t('startTimeRequired');
    }

    if (!formData.endTime) {
      newErrors.endTime = t('endTimeRequired');
    }

    if (!formData.visitType) {
      newErrors.visitType = t('visitTypeRequired');
    }

    // Validate date and time logic
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = t('endDateBeforeStartDate');
    }

    if (formData.startTime && formData.endTime && formData.startDate === formData.endDate) {
      if (formData.startTime >= formData.endTime) {
        newErrors.endTime = t('endTimeAfterStartTime');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await createUserDoctorVisit({ userId: user?._id, body: formData });
      handleClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      doctorId: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      visitType: '',
      symptoms: [],
      timezone: 5,
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px',
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black }}>
            {t('addDoctorVisit')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
              {t('visitInformation')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('visitTitle')}
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                fullWidth
                placeholder={t('visitTitlePlaceholder')}
                InputProps={{
                  sx: {
                    borderRadius: 50,
                  },
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    background: '#fff',
                    px: 1,
                  },
                }}
              />

              <TextField
                label={t('description')}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder={t('descriptionPlaceholder')}
                InputProps={{
                  sx: {
                    borderRadius: 8,
                  },
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    background: '#fff',
                    px: 1,
                  },
                }}
              />

              <FormControl fullWidth error={!!errors.doctorId}>
                <InputLabel>{t('selectDoctor')}</InputLabel>
                <Select
                  value={formData.doctorId}
                  onChange={(e) => handleInputChange('doctorId', e.target.value)}
                  label={t('selectDoctor')}
                  sx={{
                    borderRadius: 50,
                  }}
                >
                  {professionalCaregiverData?.map((doctor) => {
                    return (
                      <MenuItem key={doctor._id} value={doctor._id}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {doctor.userInfo.firstName} {doctor.userInfo.lastName}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.doctorId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.doctorId}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth error={!!errors.visitType}>
                <InputLabel>{t('visitType')}</InputLabel>
                <Select
                  value={formData.visitType}
                  onChange={(e) => handleInputChange('visitType', e.target.value)}
                  label={t('visitType')}
                  sx={{
                    borderRadius: 50,
                  }}
                >
                  {VISIT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.visitType && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.visitType}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>

          {/* Date and Time */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
              {t('schedule')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
              <TextField
                label={t('startDate')}
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                error={!!errors.startDate}
                helperText={errors.startDate}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  sx: {
                    borderRadius: 50,
                  },
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    background: '#fff',
                    px: 1,
                  },
                }}
              />

              <TextField
                label={t('endDate')}
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                error={!!errors.endDate}
                helperText={errors.endDate}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  sx: {
                    borderRadius: 50,
                  },
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    background: '#fff',
                    px: 1,
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mt: 2 }}>
              <TextField
                label={t('startTime')}
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                error={!!errors.startTime}
                helperText={errors.startTime}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  sx: {
                    borderRadius: 50,
                  },
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    background: '#fff',
                    px: 1,
                  },
                }}
              />

              <TextField
                label={t('endTime')}
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                error={!!errors.endTime}
                helperText={errors.endTime}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  sx: {
                    borderRadius: 50,
                  },
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    background: '#fff',
                    px: 1,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Symptoms */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
              {t('symptoms')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                freeSolo
                options={COMMON_SYMPTOMS}
                value=""
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleSymptomAdd(newValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('addSymptom')}
                    placeholder={t('typeOrSelectSymptom')}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        borderRadius: 50,
                      },
                    }}
                    sx={{
                      '& .MuiInputLabel-root': {
                        background: '#fff',
                        px: 1,
                      },
                    }}
                  />
                )}
              />

              {formData.symptoms.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formData.symptoms.map((symptom, index) => (
                    <Chip
                      key={index}
                      label={symptom}
                      onDelete={() => handleSymptomRemove(symptom)}
                      sx={{
                        backgroundColor: colors.lightBlue,
                        color: colors.mainBlue,
                        fontWeight: 500,
                        borderRadius: 50,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button text={t('cancel')} bg="light" onClick={handleClose} />
        <Button text={loading ? t('adding') : t('addVisit')} onClick={handleSubmit} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

export default AddModal;
